import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, ExternalLink, Briefcase, Users } from "lucide-react";
import DashboardStats from "@/components/dashboard/DashboardStats";
import DashboardChart from "@/components/dashboard/DashboardChart";
import DashboardFilters from "@/components/dashboard/DashboardFilters";
import DashboardLeadsTable from "@/components/dashboard/DashboardLeadsTable";
import LeadDetailDialog from "@/components/dashboard/LeadDetailDialog";
import AttorneysPanel from "@/components/admin/AttorneysPanel";
import ReferrersPanel from "@/components/admin/ReferrersPanel";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [state, setState] = useState("all");
  const [temperature, setTemperature] = useState("all");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rescoring, setRescoring] = useState(false);
  const [showAttorneys, setShowAttorneys] = useState(false);
  const [showReferrers, setShowReferrers] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/leads", { params: { q: q || undefined, status, state, temperature } }),
      ]);
      setStats(s.data);
      setLeads(l.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [q, status, state, temperature]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, newStatus) => {
    try {
      const { data } = await api.patch(`/admin/leads/${id}/status`, { status: newStatus });
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
      setSelected((s) => (s && s.id === id ? data : s));
      load();
      toast.success(`Marked ${newStatus}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      setSelected(null);
      load();
      toast.success("Lead deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const rescore = async (id) => {
    setRescoring(true);
    try {
      const { data } = await api.post(`/admin/leads/${id}/rescore`);
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
      setSelected((s) => (s && s.id === id ? data : s));
      load();
      toast.success(`Re-scored: ${data.temperature?.toUpperCase()} (${data.temperature_score})`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setRescoring(false);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen" data-testid="dashboard-page">
      <header className="glass-header sticky top-0 z-40">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="grid h-9 w-9 place-items-center rounded-lg text-white font-display font-black" style={{ background: "var(--brand)" }}>A</div>
            <div className="leading-tight">
              <div className="font-display text-base font-black tracking-tight text-[#11322A]">Apex MVA · Control Room</div>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C6B68]">Lead Pipeline</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="hidden text-sm font-bold text-[#5C6B68] hover:text-[#11322A] sm:inline-flex items-center gap-1.5" data-testid="dash-view-site">
              <ExternalLink className="h-3.5 w-3.5" /> View site
            </Link>
            <button
              onClick={() => setShowAttorneys(true)}
              className="hidden text-sm font-bold text-[#11322A] hover:text-[#E85D04] sm:inline-flex items-center gap-1.5"
              data-testid="dash-attorneys-btn"
            >
              <Briefcase className="h-3.5 w-3.5" /> Attorneys
            </button>
            <button
              onClick={() => setShowReferrers(true)}
              className="hidden text-sm font-bold text-[#11322A] hover:text-[#E85D04] sm:inline-flex items-center gap-1.5"
              data-testid="dash-referrers-btn"
            >
              <Users className="h-3.5 w-3.5" /> Referrers
            </button>
            <div className="hidden text-right text-xs sm:block">
              <div className="font-bold text-[#0B1317]">{user?.name || "Admin"}</div>
              <div className="text-[#5C6B68]">{user?.email}</div>
            </div>
            <button onClick={doLogout} className="inline-flex items-center gap-1.5 rounded-lg border border-[#E0E4E2] px-3 py-2 text-sm font-bold text-[#0B1317] hover:bg-white" data-testid="dash-logout">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1600px] px-4 py-8 sm:px-6">
        <DashboardStats stats={stats} />

        <div className="mt-6">
          <DashboardChart data={stats?.last_7_days} />
        </div>

        <div className="mt-6 card-soft overflow-hidden">
          <DashboardFilters
            q={q} setQ={setQ}
            status={status} setStatus={setStatus}
            temperature={temperature} setTemperature={setTemperature}
            state={state} setState={setState}
          />
          <DashboardLeadsTable leads={leads} loading={loading} onOpen={setSelected} />
        </div>
      </main>

      <LeadDetailDialog
        selected={selected}
        onClose={() => setSelected(null)}
        onUpdateStatus={updateStatus}
        onRemove={remove}
        onRescore={rescore}
        rescoring={rescoring}
        onAttorneyAssigned={(updated) => {
          setSelected((s) => (s && s.id === updated.id ? updated : s));
          setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
        }}
      />

      <AttorneysPanel open={showAttorneys} onOpenChange={setShowAttorneys} />
      <ReferrersPanel open={showReferrers} onOpenChange={setShowReferrers} />
    </div>
  );
}
